/**
 * Campaign-related API methods — core CRUD
 *
 * Sub-modules handle the rest:
 * - campaignPurchases.ts — purchase CRUD, price overrides, sell sheets
 * - campaignAnalytics.ts — analytics, portfolio, tuning, projections
 * - campaignImports.ts — imports, exports, cert entry, price review
 */

import type { Campaign, CreateCampaignInput } from '../../types/campaigns';
import type { APIClient } from './client';

// Side-effect imports: each sub-module patches APIClient.prototype
import './campaignPurchases';
import './campaignAnalytics';
import './campaignImports';

/* ------------------------------------------------------------------ */
/*  Declaration merging — core campaign CRUD                           */
/* ------------------------------------------------------------------ */

declare module './client' {
  interface APIClient {
    // Campaign CRUD
    listCampaigns(activeOnly?: boolean): Promise<Campaign[]>;
    getCampaign(id: string): Promise<Campaign>;
    createCampaign(input: CreateCampaignInput): Promise<Campaign>;
    updateCampaign(id: string, data: Partial<Campaign>, ifUnmodifiedSince?: string): Promise<Campaign>;
  }
}

/* ------------------------------------------------------------------ */
/*  Prototype implementations                                         */
/* ------------------------------------------------------------------ */

import { APIClient as _APIClient } from './client';
const proto = _APIClient.prototype;

// Campaign endpoints
proto.listCampaigns = async function (this: APIClient, activeOnly = false): Promise<Campaign[]> {
  const params = activeOnly ? '?activeOnly=true' : '';
  return this.get<Campaign[]>(`/campaigns${params}`);
};

// Single-campaign read, deliberately uncached: the edit form uses it to check
// whether the row changed underneath an open form, and a cache read cannot
// observe a write made by the psa-harvest process.
proto.getCampaign = async function (this: APIClient, id: string): Promise<Campaign> {
  return this.get<Campaign>(`/campaigns/${encodeURIComponent(id)}`);
};

proto.createCampaign = async function (this: APIClient, input: CreateCampaignInput): Promise<Campaign> {
  return this.post<Campaign>('/campaigns', input);
};

// Passing ifUnmodifiedSince (the updatedAt of the row this payload was built
// from) makes the write conditional: the server rejects it with 409 if the row
// changed in between, instead of overwriting the newer version. Read-modify-write
// callers should always pass it; the value must come from a fresh read, not from
// the React Query cache, which cannot observe a psa-harvest write.
proto.updateCampaign = async function (
  this: APIClient,
  id: string,
  data: Partial<Campaign>,
  ifUnmodifiedSince?: string,
): Promise<Campaign> {
  const qs = ifUnmodifiedSince ? `?ifUnmodifiedSince=${encodeURIComponent(ifUnmodifiedSince)}` : '';
  return this.put<Campaign>(`/campaigns/${encodeURIComponent(id)}${qs}`, data);
};
